import API from './api';
import type {
  ValidationStatusResponse,
  ValidationStats,
  ValidateSingleResponse,
  ValidateBatchResponse,
  ApplySuggestionsResponse,
} from '../types/validation';

// All validator calls go through the IPRI backend, which holds the validator API key server-side
// (the browser never sees it). The backend proxies these to the SLS validator service.
export const validationApi = {
  /** Get validation status and history for a case (from IPRI backend) */
  getStatus: async (caseId: string): Promise<ValidationStatusResponse> => {
    const response = await API.get(`/api/validation/status/${caseId}`);
    return response.data;
  },

  /** Get aggregated validation stats (from IPRI backend) */
  getStats: async (): Promise<ValidationStats> => {
    const response = await API.get('/api/validation/stats');
    return response.data;
  },

  /** Trigger validation for a single case (proxied through the IPRI backend to the validator).
   *  triggeredById is resolved server-side from the caller's JWT for cost attribution. */
  validateCase: async (caseId: string, _triggeredById?: string): Promise<ValidateSingleResponse | { status: 'processing'; caseId: string }> => {
    const response = await API.post(`/api/validation/validate/${caseId}`);
    if (response.status === 202) {
      return response.data as { status: 'processing'; caseId: string };
    }
    return response.data;
  },

  /** Trigger batch validation (proxied through the IPRI backend to the validator) */
  validateBatch: async (caseIds: string[]): Promise<ValidateBatchResponse> => {
    const response = await API.post('/api/validation/validate/batch', { caseIds });
    return response.data;
  },

  /** Apply AI suggestions from a validation result to the case */
  applySuggestions: async (
    validationResultId: string,
    fields?: string[]
  ): Promise<ApplySuggestionsResponse> => {
    const response = await API.post(
      `/api/validation/${validationResultId}/apply-suggestions`,
      fields ? { fields } : undefined
    );
    return response.data;
  },

  /** Undo an auto-applied field (restore its prior value / remove the added record) */
  undoAutoApplied: async (caseId: string, field: string): Promise<{ ok: boolean; field: string; restoredTo?: unknown }> => {
    const response = await API.post(`/api/validation/${caseId}/ai/undo`, { field });
    return response.data;
  },

  /** Redo an undone auto-applied field (re-apply the stored value) */
  redoAutoApplied: async (caseId: string, field: string): Promise<{ ok: boolean; field: string }> => {
    const response = await API.post(`/api/validation/${caseId}/ai/redo`, { field });
    return response.data;
  },

  /** Override an AI-auto-filled field with a reviewer-supplied value */
  overrideAutoApplied: async (caseId: string, field: string, value: unknown): Promise<{ ok: boolean; field: string; value: unknown }> => {
    const response = await API.post(`/api/validation/${caseId}/ai/override`, { field, value });
    return response.data;
  },

  /** Resolve a source conflict: 'proposed' applies the proposed value, 'current' keeps existing */
  resolveConflict: async (
    caseId: string,
    conflictId: string,
    choice: 'current' | 'proposed'
  ): Promise<{ ok: boolean; conflictId: string; choice: string; resolution: unknown }> => {
    const response = await API.post(`/api/validation/${caseId}/ai/conflicts/${conflictId}/resolve`, { choice });
    return response.data;
  },
};
